/**
 * Theme Requirements Aggregation for Dolben
 * 
 * Extends the base rise theme requirements with Dolben specific
 * component requirements.
 */

import type { ComponentRequirements, DataFieldPath } from '@vibesites/tokens';
import { defineComponentRequirements } from '@vibesites/tokens';
import { componentRequirements as baseRequirements } from '@vibesites/theme-rise/requirements';

/**
 * Dolben component requirement overrides.
 * Add requirements for any override components here.
 *
 * Company mono logo fields are cast through DataFieldPath — same parent-theme
 * limitation as `@vibesites/theme-rise/requirements` (fields exist on property
 * objects but aren't in the DataFieldPath union yet).
 */
const dolbenOverrides: Record<string, ComponentRequirements> = {
  AmenitiesList: defineComponentRequirements('AmenitiesList', {
    designTokens: [
      'color.primary',
      'color.secondary',
      'color.background',
      'color.text',
      'font.heading',
      'font.body',
    ],
    dataTokens: [
      { field: 'amenities', required: false, category: 'collections' },
      { field: 'photo_gallery', required: false, category: 'collections' },
    ],
  }),
  ApplyCtaBanner: defineComponentRequirements('ApplyCtaBanner', {
    designTokens: ['color.primary', 'font.body'],
    dataTokens: [
      { field: 'property_apply_now_url', required: false, category: 'urls' },
    ],
  }),
  ContactForm: defineComponentRequirements('ContactForm', {
    designTokens: ['color.text', 'color.surface', 'font.body'],
    dataTokens: [
      { field: 'photo_gallery' as DataFieldPath, required: false, category: 'collections' },
    ],
  }),
  ContactInfoCards: defineComponentRequirements('ContactInfoCards', {
    designTokens: ['color.text', 'color.background', 'font.body'],
    dataTokens: [
      { field: 'property_call_tracking_phone', required: false, category: 'contact' },
      { field: 'property_full_address', required: false, category: 'contact' },
      { field: 'office_hours' as DataFieldPath, required: false, category: 'collections' },
    ],
  }),
  Footer: defineComponentRequirements('Footer', {
    designTokens: ['color.text', 'color.border', 'font.heading', 'font.body'],
    dataTokens: [
      { field: 'property_call_tracking_phone', required: false, category: 'contact' },
      { field: 'property_full_address', required: false, category: 'contact' },
      { field: 'property_mono_white_logo', required: false, category: 'branding' },
      { field: 'property_mono_black_logo', required: false, category: 'branding' },
      { field: 'property_company_logo', required: false, category: 'branding' },
      {
        field: 'property_company_mono_white_logo' as DataFieldPath,
        required: false,
        category: 'branding',
      },
      {
        field: 'property_company_mono_black_logo' as DataFieldPath,
        required: false,
        category: 'branding',
      },
      { field: 'property_company_url', required: false, category: 'branding' },
      { field: 'property_facebook_url', required: false, category: 'urls' },
      { field: 'property_instagram_url', required: false, category: 'urls' },
      { field: 'office_hours' as DataFieldPath, required: false, category: 'collections' },
    ],
  }),
  LifestyleIntro: defineComponentRequirements('LifestyleIntro', {
    designTokens: [
      'color.primary',
      'color.secondary',
      'color.background',
      'color.text',
      'font.body',
    ],
    dataTokens: [
      { field: 'property_name', required: false, category: 'branding' },
      { field: 'property_address_city', required: false, category: 'contact' },
    ],
  }),
  NeighborhoodDiscover: defineComponentRequirements('NeighborhoodDiscover', {
    designTokens: [
      'color.primary',
      'color.secondary',
      'color.background',
      'color.text',
      'font.body',
    ],
    dataTokens: [
      { field: 'property_name', required: false, category: 'branding' },
      { field: 'property_address_city', required: false, category: 'contact' },
      { field: 'property_full_address', required: false, category: 'contact' },
      { field: 'property_address_latitude', required: false, category: 'contact' },
      { field: 'property_address_longitude', required: false, category: 'contact' },
      {
        field: 'property_home_neighborhood_blurb' as DataFieldPath,
        required: false,
        category: 'content',
      },
    ],
  }),
  ReviewsCallout: defineComponentRequirements('ReviewsCallout', {
    designTokens: [
      'color.primary',
      'color.text',
      'color.border',
      'font.heading',
      'font.body',
    ],
    dataTokens: [
      { field: 'property_name', required: false, category: 'branding' },
      { field: 'reviews' as DataFieldPath, required: false, category: 'collections' },
    ],
  }),
  NeighborhoodMap: defineComponentRequirements('NeighborhoodMap', {
    designTokens: [
      'color.primary',
      'color.text',
      'color.surface',
      'color.border',
      'font.heading',
      'font.body',
    ],
    dataTokens: [
      { field: 'property_icon', required: false, category: 'branding' },
      { field: 'property_address_latitude', required: false, category: 'contact' },
      { field: 'property_address_longitude', required: false, category: 'contact' },
      {
        field: 'neighborhood_pois' as DataFieldPath,
        required: false,
        category: 'collections',
      },
    ],
  }),
  VirtualTourEmbed: defineComponentRequirements('VirtualTourEmbed', {
    designTokens: ['color.text', 'color.surface', 'color.border', 'font.body'],
    dataTokens: [
      {
        field: 'property_virtual_tour_url' as DataFieldPath,
        required: false,
        category: 'urls',
      },
    ],
  }),
  Gallery: defineComponentRequirements('Gallery', {
    designTokens: ['color.primary', 'color.secondary', 'color.text', 'font.body'],
    dataTokens: [
      { field: 'property_gallery_blurb' as DataFieldPath, required: false, category: 'content' },
      { field: 'photo_gallery' as DataFieldPath, required: false, category: 'collections' },
    ],
  }),
};

/**
 * All component requirements for the Dolben theme.
 * Inherits from rise theme and overrides with Dolben components.
 */
export const componentRequirements: Record<string, ComponentRequirements> = {
  ...baseRequirements,
  ...dolbenOverrides,
};

/**
 * Get requirements for a specific component.
 */
export function getComponentRequirements(componentName: string): ComponentRequirements | undefined {
  return componentRequirements[componentName];
}

/**
 * Get all component names that have requirements defined.
 */
export function getRegisteredComponentNames(): string[] {
  return Object.keys(componentRequirements);
}
